import { useState, useRef, useCallback, useEffect } from "react";
import { ocrExtract } from "./lib/ocr-engine.js";
import { pdfToImages } from "./lib/pdf-utils.js";
import { rotateImage90 } from "./lib/crop-utils.js";
import SelectionOverlay from "./SelectionOverlay.jsx";
import "./styles/animations.css";

export default function DocumentScanner({ config, onComplete, onCancel }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [imageSrc, setImageSrc] = useState(null);
  const [pdfPages, setPdfPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const [rotating, setRotating] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);

  const fileInputRef = useRef(null);
  const imgRef = useRef(null);

  const handleRotate = async () => {
    if (!imageSrc || rotating) return;
    setRotating(true);
    const rotated = await rotateImage90(imageSrc);
    setImageSrc(rotated);
    setRotating(false);
  };

  const steps = config.steps;
  const step = steps[currentStep];
  const filledCount = steps.filter((s) => formData[s.field]).length;

  useEffect(() => {
    setTimeout(() => fileInputRef.current?.click(), 200);
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOriginalFile(file);

    if (file.type === "application/pdf") {
      setLoadingPdf(true);
      try {
        const pages = await pdfToImages(file);
        setPdfPages(pages);
        setImageSrc(pages[0].src);
        setCurrentPage(0);
      } catch (err) {
        console.error("PDF error:", err);
        alert("Erreur de lecture du PDF. Essayez avec une image.");
      }
      setLoadingPdf(false);
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => setImageSrc(ev.target.result);
      reader.readAsDataURL(file);
      setPdfPages(null);
    }
  };

  const handleCapture = useCallback(async (croppedBase64) => {
    setProcessing(true);
    const value = await ocrExtract(croppedBase64, step.ocrHint);
    setFormData((prev) => ({ ...prev, [step.field]: value }));
    setProcessing(false);

    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      setCompleted(true);
      setTimeout(() => {
        const finalData = { ...formData, [step.field]: value };
        onComplete?.({ data: finalData, file: originalFile });
      }, 2500);
    }
  }, [currentStep, step, steps, formData, onComplete, originalFile]);

  const handleSkip = () => {
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
  };

  const handleEditField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ── Upload prompt (no image loaded yet) ──
  if (!imageSrc && !loadingPdf) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 20 }}>
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFile} style={{ display: "none" }} />
        <div style={{ fontSize: 56 }}>{config.icon}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#1E293B", fontFamily: "'DM Sans', sans-serif" }}>{config.title}</div>
        <div style={{ fontSize: 14, color: "#64748B", fontFamily: "'DM Sans', sans-serif" }}>{config.description}</div>
        <button onClick={() => fileInputRef.current?.click()} style={{
          padding: "12px 28px", background: "linear-gradient(135deg, #F59E0B, #D97706)", border: "none",
          borderRadius: 99, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif", boxShadow: "0 4px 16px rgba(245,158,11,0.3)",
        }}>
          {"\uD83D\uDCC2"} Charger un document (image ou PDF)
        </button>
        <button onClick={onCancel} style={{
          padding: "8px 20px", background: "none", border: "1px solid #D1D5DB", borderRadius: 99,
          color: "#64748B", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
        }}>
          Annuler
        </button>
      </div>
    );
  }

  // ── PDF loading spinner ──
  if (loadingPdf) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16 }}>
        <div style={{ width: 40, height: 40, border: "3px solid #E5E7EB", borderTopColor: "#F59E0B", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <div style={{ fontSize: 14, color: "#64748B", fontFamily: "'DM Sans', sans-serif" }}>{"Conversion du PDF en cours\u2026"}</div>
      </div>
    );
  }

  // ── Main scanner layout ──
  return (
    <div style={{ display: "flex", height: "100%", fontFamily: "'DM Sans', sans-serif" }}>
      {/* ── Left: Form fields ── */}
      <div style={{ width: 340, minWidth: 340, borderRight: "1px solid #E5E7EB", background: "#fff", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>{config.icon}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>{config.title}</div>
            <div style={{ fontSize: 11, color: "#94A3B8" }}>{filledCount}/{steps.length} champs capturés</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 2, padding: "0 20px", paddingTop: 12 }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 99,
              background: formData[s.field] ? s.color : currentStep === i ? `${s.color}50` : "#E5E7EB",
              transition: "all 0.4s",
            }} />
          ))}
        </div>

        {/* Fields list */}
        <div style={{ flex: 1, overflow: "auto", padding: "12px 20px" }}>
          {steps.map((s, idx) => {
            const isFilled = !!formData[s.field];
            const isActive = currentStep === idx && !completed;
            return (
              <div key={s.field} style={{
                marginBottom: 10, padding: "10px 12px", borderRadius: 10,
                border: isActive ? `2px solid ${s.color}` : isFilled ? `1.5px solid ${s.color}30` : "1.5px solid #F1F5F9",
                background: isActive ? `${s.color}06` : isFilled ? `${s.color}04` : "#FAFBFC",
                transition: "all 0.3s",
                animation: isFilled ? "popIn 0.35s ease" : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 13 }}>{s.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? s.color : isFilled ? s.color : "#94A3B8" }}>{s.label}</span>
                  {isFilled && <span style={{ marginLeft: "auto", fontSize: 10, color: s.color, fontWeight: 600 }}>{"\u2713"}</span>}
                  {isActive && !isFilled && (
                    <span style={{
                      marginLeft: "auto", fontSize: 9, background: `${s.color}15`, color: s.color,
                      padding: "2px 7px", borderRadius: 99, fontWeight: 600,
                    }}>EN COURS</span>
                  )}
                </div>
                <input
                  value={formData[s.field] || ""}
                  onChange={(e) => handleEditField(s.field, e.target.value)}
                  placeholder={"\u2014"}
                  style={{
                    width: "100%", border: "none", outline: "none", background: "transparent",
                    fontSize: 13, fontWeight: 500, color: "#1E293B", fontFamily: "'DM Sans', sans-serif",
                    padding: 0,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Bottom actions */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #E5E7EB", display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "9px 0", background: "#F1F5F9", border: "none", borderRadius: 8,
            fontSize: 12, fontWeight: 600, color: "#64748B", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>
            Annuler
          </button>
          {!completed && (
            <button onClick={handleSkip} style={{
              flex: 1, padding: "9px 0", background: "#FEF3C7", border: "none", borderRadius: 8,
              fontSize: 12, fontWeight: 600, color: "#92400E", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            }}>
              Passer {"\u25B8"}
            </button>
          )}
          {filledCount > 0 && (
            <button onClick={() => onComplete?.({ data: formData, file: originalFile })} style={{
              flex: 1, padding: "9px 0", background: "linear-gradient(135deg, #10B981, #059669)", border: "none", borderRadius: 8,
              fontSize: 12, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            }}>
              Valider {"\u2713"}
            </button>
          )}
        </div>
      </div>

      {/* ── Right: Document viewer ── */}
      <div style={{ flex: 1, background: "#0F172A", position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto" }}>
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFile} style={{ display: "none" }} />

        {/* Rotate button */}
        <button
          onClick={handleRotate}
          disabled={rotating || processing}
          title="Rotation 90°"
          style={{
            position: "absolute", top: 14, right: 14, zIndex: 20,
            width: 36, height: 36, borderRadius: 8,
            background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)",
            color: rotating ? "#475569" : "#CBD5E1", fontSize: 18,
            cursor: rotating ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(8px)", transition: "all 0.2s",
          }}
        >
          {"\u21BB"}
        </button>

        {/* PDF page navigation */}
        {pdfPages && pdfPages.length > 1 && (
          <div style={{
            position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
            display: "flex", alignItems: "center", gap: 8, zIndex: 20,
            background: "rgba(0,0,0,0.6)", padding: "6px 16px", borderRadius: 99, backdropFilter: "blur(8px)",
          }}>
            <button onClick={() => { const p = Math.max(0, currentPage - 1); setCurrentPage(p); setImageSrc(pdfPages[p].src); }}
              disabled={currentPage === 0}
              style={{ background: "none", border: "none", color: currentPage === 0 ? "#475569" : "#fff", fontSize: 16, cursor: "pointer", padding: "2px 8px" }}>
              {"\u25C0"}
            </button>
            <span style={{ color: "#CBD5E1", fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
              Page {currentPage + 1} / {pdfPages.length}
            </span>
            <button onClick={() => { const p = Math.min(pdfPages.length - 1, currentPage + 1); setCurrentPage(p); setImageSrc(pdfPages[p].src); }}
              disabled={currentPage === pdfPages.length - 1}
              style={{ background: "none", border: "none", color: currentPage === pdfPages.length - 1 ? "#475569" : "#fff", fontSize: 16, cursor: "pointer", padding: "2px 8px" }}>
              {"\u25B6"}
            </button>
          </div>
        )}

        {/* Step sidebar */}
        <div style={{
          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
          display: "flex", flexDirection: "column", gap: 6, zIndex: 20,
        }}>
          {steps.map((s, idx) => (
            <button key={idx} onClick={() => !completed && setCurrentStep(idx)} style={{
              width: 32, height: 32, borderRadius: 8,
              background: formData[s.field] ? s.color : currentStep === idx ? `${s.color}35` : "rgba(255,255,255,0.06)",
              border: currentStep === idx && !formData[s.field] ? `2px solid ${s.color}` : "2px solid transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: formData[s.field] ? 13 : 14, color: "#fff", cursor: completed ? "default" : "pointer",
              transition: "all 0.3s", backdropFilter: "blur(4px)",
            }}>
              {formData[s.field] ? "\u2713" : s.icon}
            </button>
          ))}
        </div>

        {/* Image display */}
        {/* width: fit-content — le cadre épouse l'image, donc le calque de sélection aussi.
            En bloc pleine largeur, une image plus petite que 82 % laissait le calque déborder
            et le rognage embarquait le champ voisin (date « /2014 » en bout de champ). */}
        <div style={{ position: "relative", width: "fit-content", maxWidth: "82%", margin: "40px auto" }}>
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Document"
            style={{
              maxWidth: "100%", borderRadius: 10,
              boxShadow: "0 8px 40px rgba(0,0,0,0.5)", display: "block",
              userSelect: "none", pointerEvents: "none",
            }}
            draggable={false}
          />
          {!completed && step && (
            <SelectionOverlay
              step={step}
              onCapture={handleCapture}
              imgRef={imgRef}
              processing={processing}
            />
          )}

          {/* Success overlay */}
          {completed && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(16,185,129,0.88)",
              borderRadius: 10, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              animation: "successFade 0.4s ease", backdropFilter: "blur(4px)",
            }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%", background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 36, animation: "checkmark 0.6s cubic-bezier(0.34,1.56,0.64,1)", marginBottom: 16, color: "#fff",
              }}>{"\u2713"}</div>
              <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>
                Données complètes !
              </div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>
                {filledCount + 1} champs capturés avec succès
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
